import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Mail, ShieldAlert, Phone, MapPin, Loader2, Save, ArrowLeft, Building2, CheckCircle2, FileText, Headphones, Edit } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import TokenExpiredError from '../components/TokenExpiredError';
import CustomerSidebar from '../features/customer/components/CustomerSidebar';
import CustomerBottomNavbar from '../features/customer/components/CustomerBottomNavbar';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { orderSessionService } from '../services/api/orderSessionService';
import { publicCustomerService } from '../services/api/publicCustomerService';

const CustomerProfilePage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const [customerData, setCustomerData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);

    // Form fields state
    const [name, setName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [trn, setTrn] = useState('');
    const [address, setAddress] = useState('');

    // Field-specific validation errors
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        const loadProfileData = async () => {
            let activeToken = token;
            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                activeToken = localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');
            }

            if (!activeToken || activeToken === 'null' || activeToken === 'undefined') {
                console.log('[CustomerProfilePage] No token found, redirecting to login...');
                navigate('/order/login');
                return;
            }

            try {
                // 1. Fetch Session Context to validate token and identify Tenant
                const session = await orderSessionService.getSessionContext(activeToken);
                if (!session.valid) {
                    setError(session.errorMessage || 'Session expired');
                    setLoading(false);
                    return;
                }

                setSupplierData(session.tenant);

                // 2. Fetch fresh Profile Details
                const profile = await publicCustomerService.getCustomerById(session.customer.publicId, session.tenant.supplierId);
                setCustomerData(profile);

                // Initialize form values
                setName(profile.name || '');
                setContactPerson(profile.contactPerson || '');
                setMobile(profile.mobile || '');
                setEmail(profile.email || '');
                setTrn(profile.trn || '');
                setAddress(profile.address || '');

                setLoading(false);

                // Clean token from URL query parameters so back-button doesn't auto-relogin
                if (token) {
                    if (token !== 'null' && token !== 'undefined') {
                        localStorage.setItem('customer_token', token);
                    }
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('token');
                    setSearchParams(newParams, { replace: true });
                }
            } catch (err) {
                console.error('[CustomerProfilePage] Error loading profile details:', err);
                setError('Failed to load profile information. Please try again.');
                setLoading(false);
            }
        };

        loadProfileData();
    }, [token, searchParams, setSearchParams]);

    const validateForm = () => {
        const errors = {};

        if (!name.trim()) {
            errors.name = 'Business Name is required';
        }

        if (email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                errors.email = 'Please enter a valid email address';
            }
        }

        if (mobile.trim()) {
            const cleanPhone = mobile.replace(/\s+/g, '');
            if (cleanPhone.length < 9) {
                errors.mobile = 'Mobile number must be at least 9 digits';
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSuccessMessage(null);

        if (!validateForm()) return;

        setSaving(true);
        try {
            const activeToken = token && token !== 'null' && token !== 'undefined' ? token : (localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token'));
            const payload = {
                name: name.trim(),
                contactPerson: contactPerson.trim(),
                mobileNumber: mobile.trim(),
                email: email.trim(),
                trn: trn.trim(),
                address: address.trim()
            };

            await publicCustomerService.updateCustomerProfile(activeToken, payload);

            // Update local state so layout headers immediately sync
            setCustomerData(prev => ({
                ...prev,
                name: name.trim(),
                customerName: name.trim(),
                contactPerson: contactPerson.trim(),
                mobile: mobile.trim(),
                email: email.trim(),
                trn: trn.trim(),
                address: address.trim()
            }));

            setSuccessMessage('Profile details updated successfully!');
            setIsEditing(false);
            
            // Clear success alert after 4 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 4000);
        } catch (err) {
            console.error('[CustomerProfilePage] Error saving profile:', err);
            setFieldErrors({ submit: 'Failed to save changes. Please check your network and try again.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (customerData) {
            setName(customerData.name || customerData.customerName || '');
            setContactPerson(customerData.contactPerson || '');
            setMobile(customerData.mobile || customerData.mobileNumber || '');
            setEmail(customerData.email || '');
            setTrn(customerData.trn || '');
            setAddress(customerData.address || '');
        }
        setFieldErrors({});
    };

    if (loading) return <LoadingSpinner message="Retrieving your profile details..." />;
    if (error) return <TokenExpiredError message={error} />;

    const activeToken = token && token !== 'null' && token !== 'undefined' ? token : (localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token'));

    return (
        <div className="flex h-[100dvh] w-screen bg-gray-50 dark:bg-slate-900 overflow-hidden font-sans">
            {/* Desktop Left Sidebar */}
            <CustomerSidebar 
                activeView="profile"
                token={activeToken}
                customerData={customerData}
                supplierData={supplierData}
            />

            {/* Main Content Pane */}
            <div className="flex-1 flex flex-col min-w-0 h-[100dvh] relative pb-customer-nav lg:pb-0">
                {/* Header - Styled consistently with CustomerHeader */}
                <header className="bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700/50 px-4 py-1.5 md:px-6 md:py-2 flex-none z-30">
                    <div className="w-full flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate(`/shop?token=${activeToken}`)}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer mr-1"
                                    aria-label="Back to shop"
                                >
                                    <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-slate-300" />
                                </button>
                                <img
                                    src="/logo.jpg"
                                    alt="Logo"
                                    className="w-10 h-10 rounded-lg object-contain"
                                />
                                <img
                                    src="/name.jpg"
                                    alt="Yaseero"
                                    className="h-8 object-contain"
                                />
                            </div>
                            {!isEditing ? (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="text-xs font-bold text-white bg-cyan-500 hover:bg-cyan-600 px-4 py-2 rounded-xl shadow-md transition-all active:scale-[0.98] flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Edit className="w-3.5 h-3.5" />
                                    Edit Profile
                                </button>
                            ) : (
                                <span className="text-xs font-black text-cyan-600 uppercase tracking-widest bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-100 dark:border-cyan-900/30 px-3 py-1.5 rounded-xl animate-pulse">
                                    Editing Profile
                                </span>
                            )}
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg px-3 py-1.5 mb-2">
                            <h2 className="font-bold text-gray-800 dark:text-gray-200 text-base">
                                {supplierData?.businessName || 'Supplier'}
                            </h2>
                        </div>
                    </div>
                </header>

                {/* Scrollable Form Container */}
                <div className="flex-1 overflow-hidden flex flex-col p-4 md:p-6 bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                        <div className="w-full max-w-2xl mx-auto pb-28 flex flex-col gap-6">
                            
                            {successMessage && (
                                <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 p-4 rounded-2xl shadow-sm text-emerald-800 dark:text-emerald-300 transition-all duration-300">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                    <p className="text-xs font-bold">{successMessage}</p>
                                </div>
                            )}

                            {fieldErrors.submit && (
                                <div className="flex items-center gap-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 p-4 rounded-2xl shadow-sm text-rose-800 dark:text-rose-300">
                                    <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                                    <p className="text-xs font-bold">{fieldErrors.submit}</p>
                                </div>
                            )}

                            <form onSubmit={handleSave} className="flex flex-col gap-6">
                                {/* Card 1: Business Details */}
                                <Card className="p-6 border border-slate-100 dark:border-slate-800/80 flex flex-col gap-4">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1 flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-cyan-600" />
                                            Business Details
                                        </h3>
                                        <p className="text-[11px] text-slate-400 dark:text-slate-500">Official business registry names and tax criteria.</p>
                                    </div>
                                    <hr className="border-slate-50 dark:border-slate-700/50" />

                                    <div className="flex flex-col gap-3">
                                        <Input
                                            label="Business / Customer Name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter registered business name"
                                            error={fieldErrors.name}
                                            icon={Building2}
                                            required
                                            disabled={!isEditing}
                                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 disabled:opacity-75 disabled:bg-slate-50"
                                        />

                                        <Input
                                            label="Tax Registration Number (TRN)"
                                            value={trn}
                                            onChange={(e) => setTrn(e.target.value)}
                                            placeholder="Enter TRN (e.g. 100xxxxxxxxxxxx)"
                                            icon={FileText}
                                            disabled={!isEditing}
                                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 font-mono disabled:opacity-75 disabled:bg-slate-50"
                                        />
                                    </div>
                                </Card>

                                {/* Card 2: Contact Details */}
                                <Card className="p-6 border border-slate-100 dark:border-slate-800/80 flex flex-col gap-4">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1 flex items-center gap-2">
                                            <User className="w-4 h-4 text-cyan-600" />
                                            Contact Information
                                        </h3>
                                        <p className="text-[11px] text-slate-400 dark:text-slate-500">Primary point of contact for ordering and logistics notifications.</p>
                                    </div>
                                    <hr className="border-slate-50 dark:border-slate-700/50" />

                                    <div className="flex flex-col gap-4">
                                        <Input
                                            label="Contact Person"
                                            value={contactPerson}
                                            onChange={(e) => setContactPerson(e.target.value)}
                                            placeholder="Enter primary contact name"
                                            icon={User}
                                            disabled={!isEditing}
                                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 disabled:opacity-75 disabled:bg-slate-50"
                                        />

                                        <div className="flex flex-col gap-1.5">
                                            <Input
                                                label="Contact Mobile"
                                                value={mobile}
                                                onChange={(e) => setMobile(e.target.value)}
                                                placeholder="Enter contact mobile (+971...)"
                                                error={fieldErrors.mobile}
                                                icon={Phone}
                                                disabled={!isEditing}
                                                className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 disabled:opacity-75 disabled:bg-slate-50"
                                            />
                                            <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/30 p-2.5 rounded-xl border border-slate-100/60 dark:border-slate-800/40 font-medium">
                                                💡 **Important Note**: This field represents your primary contact mobile number for deliveries, invoices, and messaging. Changing this will **not** alter your portal credentials or the PIN code you use to log in.
                                            </p>
                                        </div>

                                        <Input
                                            label="Email Address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Enter email address"
                                            error={fieldErrors.email}
                                            icon={Mail}
                                            disabled={!isEditing}
                                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 disabled:opacity-75 disabled:bg-slate-50"
                                        />
                                    </div>
                                </Card>

                                {/* Card 3: Location Details */}
                                <Card className="p-6 border border-slate-100 dark:border-slate-800/80 flex flex-col gap-4">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1 flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-cyan-600" />
                                            Primary Delivery Address
                                        </h3>
                                        <p className="text-[11px] text-slate-400 dark:text-slate-500">Default physical address used for dispatching orders.</p>
                                    </div>
                                    <hr className="border-slate-50 dark:border-slate-700/50" />

                                    <div className="flex flex-col gap-3">
                                        <Input
                                            label="Delivery Address"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            placeholder="Enter physical address details"
                                            icon={MapPin}
                                            disabled={!isEditing}
                                            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 disabled:opacity-75 disabled:bg-slate-50"
                                        />
                                    </div>
                                </Card>

                                {/* Card 4: Supplier / Customer Care Details */}
                                <Card className="p-6 border border-slate-100 dark:border-slate-800/80 flex flex-col gap-4">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1 flex items-center gap-2">
                                            <Headphones className="w-4 h-4 text-cyan-600" />
                                            Customer Care & Supplier Details
                                        </h3>
                                        <p className="text-[11px] text-slate-400 dark:text-slate-500">Need help? Contact your supplier or assigned sales representatives below.</p>
                                    </div>
                                    <hr className="border-slate-50 dark:border-slate-700/50" />

                                    <div className="flex flex-col gap-4">
                                        {/* Supplier Direct Details */}
                                        <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-2xl border border-slate-100/80 dark:border-slate-800 flex flex-col gap-2.5">
                                            <p className="text-xs font-black text-slate-400 dark:text-slate-505 uppercase tracking-wider">Supplier Customer Service</p>
                                            <div className="flex flex-col gap-2">
                                                {supplierData?.phone && (
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                                                            <Phone className="w-3.5 h-3.5 text-slate-400" /> Phone:
                                                        </span>
                                                        <a href={`tel:${supplierData.phone}`} className="text-cyan-600 dark:text-sky-400 font-extrabold hover:underline">
                                                            {supplierData.phone}
                                                        </a>
                                                    </div>
                                                )}
                                                {supplierData?.email && (
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                                                            <Mail className="w-3.5 h-3.5 text-slate-400" /> Email:
                                                        </span>
                                                        <a href={`mailto:${supplierData.email}`} className="text-cyan-600 dark:text-sky-400 font-extrabold hover:underline">
                                                            {supplierData.email}
                                                        </a>
                                                    </div>
                                                )}
                                                {supplierData?.address && (
                                                    <div className="flex items-start justify-between text-xs gap-4">
                                                        <span className="text-slate-500 font-semibold flex items-center gap-1.5 shrink-0">
                                                            <MapPin className="w-3.5 h-3.5 text-slate-400" /> Address:
                                                        </span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-right">
                                                            {supplierData.address}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Assigned Sales Persons */}
                                        {customerData?.assignedSalesPersons && customerData.assignedSalesPersons.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">Assigned Sales Representatives</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {customerData.assignedSalesPersons.map(sp => (
                                                        <div key={sp.publicId} className="bg-white dark:bg-slate-800 p-3.5 rounded-2xl border border-slate-100 dark:border-slate-750/80 flex flex-col gap-1.5 shadow-sm">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 text-cyan-600 flex items-center justify-center text-[10px] font-black border border-cyan-100/50">
                                                                    {sp.fullName?.charAt(0) || 'S'}
                                                                </div>
                                                                <span className="text-xs font-extrabold text-slate-800 dark:text-slate-200 truncate">{sp.fullName}</span>
                                                            </div>
                                                            {sp.phone ? (
                                                                <a href={`tel:${sp.phone}`} className="text-xs font-bold text-cyan-600 dark:text-sky-400 hover:underline flex items-center gap-1.5 mt-0.5">
                                                                    <Phone className="w-3 h-3" />
                                                                    {sp.phone}
                                                                </a>
                                                            ) : (
                                                                <span className="text-[10px] text-slate-400 font-medium">No contact phone available</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Delegated Sales Persons */}
                                        {customerData?.delegatedSalesPersons && customerData.delegatedSalesPersons.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">Delegated Representatives</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {customerData.delegatedSalesPersons.map(sp => (
                                                        <div key={sp.publicId} className="bg-white dark:bg-slate-800 p-3.5 rounded-2xl border border-slate-100 dark:border-slate-750/80 flex flex-col gap-1.5 shadow-sm">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-600 flex items-center justify-center text-[10px] font-black border border-amber-100/50">
                                                                    {sp.fullName?.charAt(0) || 'D'}
                                                                </div>
                                                                <span className="text-xs font-extrabold text-slate-800 dark:text-slate-200 truncate">{sp.fullName}</span>
                                                            </div>
                                                            {sp.phone ? (
                                                                <a href={`tel:${sp.phone}`} className="text-xs font-bold text-cyan-600 dark:text-sky-400 hover:underline flex items-center gap-1.5 mt-0.5">
                                                                    <Phone className="w-3 h-3" />
                                                                    {sp.phone}
                                                                </a>
                                                            ) : (
                                                                <span className="text-[10px] text-slate-400 font-medium">No contact phone available</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card>

                                {/* Submit Button */}
                                {isEditing && (
                                    <div className="mt-2 flex gap-3 justify-end items-center animate-in fade-in slide-in-from-bottom-2 duration-200">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleCancel}
                                            disabled={saving}
                                            className="border border-slate-200 dark:border-slate-700"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={saving}
                                            className="bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/20 px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2"
                                        >
                                            {saving ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Saving Profile...
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="w-4 h-4" />
                                                    Save Changes
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </form>

                            {/* Page Inline Footer */}
                            <footer className="py-6 text-center border-t border-slate-100/60 mt-4">
                                <p className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase tracking-wider">
                                    Securely powered by <span className="text-slate-500 font-extrabold">Yaseero</span>
                                </p>
                            </footer>
                        </div>
                    </div>
                </div>

                {/* Mobile / Tablet Bottom Navigation */}
                <CustomerBottomNavbar 
                    activeView="profile"
                    token={activeToken}
                    supplierData={supplierData}
                />
            </div>
        </div>
    );
};

export default CustomerProfilePage;
